import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  BookOpenCheck,
  Check,
  Copy,
  Loader2,
  Pencil,
  Plus,
  Search,
  Sparkles,
  Star,
  Trash2,
  TriangleAlert,
  Wand2,
} from 'lucide-react';
import {
  CATEGORIAS_PROMPT,
  DESCRICAO_CATEGORIA_PROMPT,
  ROTULO_CATEGORIA_PROMPT,
  type CategoriaPrompt,
} from '@miolos/core';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Campo } from '@/components/ui/label';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Input, Textarea } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LinhaSwitch } from '@/components/ui/switch';
import {
  api,
  type DadosPrompt,
  type PromptExemplo,
  type PromptSalvo,
} from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import { cn } from '@/lib/utils';

const PROMPT_VAZIO: DadosPrompt = {
  titulo: '',
  categoria: 'ORGANIZACAO',
  objetivo: '',
  conteudo: '',
  modelo: '',
  resultado: '',
  tags: [],
  favorito: false,
};

/**
 * Biblioteca de prompts de IA.
 *
 * Reúne duas coisas: o material didático (o que é IA, o que é prompt, anatomia,
 * métodos e erros comuns) e o cadastro dos prompts que o usuário testou e deu
 * certo — com categoria, modelo usado e o que aprendeu no resultado.
 */
export function Prompts() {
  const { usuario } = useAuth();
  const [prompts, setPrompts] = useState<PromptSalvo[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [categoria, setCategoria] = useState<CategoriaPrompt | 'TODAS'>('TODAS');
  const [somenteFavoritos, setSomenteFavoritos] = useState(false);
  const [copiaDo, setCopiaDo] = useState<string | null>(null);

  const [editando, setEditando] = useState<PromptSalvo | null>(null);
  const [formularioAberto, setFormularioAberto] = useState(false);
  const [dados, setDados] = useState<DadosPrompt>(PROMPT_VAZIO);
  const [salvando, setSalvando] = useState(false);

  const [guiaAberto, setGuiaAberto] = useState(false);
  const [exemplos, setExemplos] = useState<PromptExemplo[]>([]);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro(null);
    try {
      const resposta = await api.listarPrompts({
        busca: busca.trim() || undefined,
        categoria: categoria === 'TODAS' ? undefined : categoria,
        favoritos: somenteFavoritos || undefined,
      });
      setPrompts(resposta.prompts);
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível carregar sua biblioteca.');
    } finally {
      setCarregando(false);
    }
  }, [busca, categoria, somenteFavoritos]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  useEffect(() => {
    api
      .guiaDePrompts()
      .then((resposta) => setExemplos(resposta.exemplos))
      .catch(() => undefined);
  }, []);

  const abrirNovo = () => {
    setEditando(null);
    setDados(PROMPT_VAZIO);
    setFormularioAberto(true);
  };

  const abrirEdicao = (prompt: PromptSalvo) => {
    setEditando(prompt);
    setDados({
      titulo: prompt.titulo,
      categoria: prompt.categoria,
      objetivo: prompt.objetivo,
      conteudo: prompt.conteudo,
      modelo: prompt.modelo,
      resultado: prompt.resultado,
      tags: prompt.tags,
      favorito: prompt.favorito,
    });
    setFormularioAberto(true);
  };

  const usarExemplo = (exemplo: PromptExemplo) => {
    setEditando(null);
    setDados({
      titulo: exemplo.titulo,
      categoria: exemplo.categoria,
      objetivo: exemplo.objetivo,
      conteudo: exemplo.conteudo,
      modelo: '',
      resultado: '',
      tags: [],
      favorito: false,
    });
    setFormularioAberto(true);
    setGuiaAberto(false);
  };

  const salvar = async () => {
    setSalvando(true);
    setErro(null);
    try {
      if (editando) await api.atualizarPrompt(editando.id, dados);
      else await api.criarPrompt(dados);
      setFormularioAberto(false);
      setAviso(editando ? 'Prompt atualizado.' : 'Prompt guardado na sua biblioteca.');
      await carregar();
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível salvar o prompt.');
    } finally {
      setSalvando(false);
    }
  };

  const copiar = async (prompt: PromptSalvo) => {
    try {
      await navigator.clipboard.writeText(prompt.conteudo);
      setCopiaDo(prompt.id);
      window.setTimeout(() => setCopiaDo(null), 1600);
      await api.marcarUsoDoPrompt(prompt.id);
    } catch {
      setErro('Não foi possível copiar. Selecione o texto e copie manualmente.');
    }
  };

  const alternarFavorito = async (prompt: PromptSalvo) => {
    try {
      await api.atualizarPrompt(prompt.id, { favorito: !prompt.favorito });
      await carregar();
    } catch {
      setErro('Não foi possível atualizar o favorito.');
    }
  };

  const excluir = async (prompt: PromptSalvo) => {
    try {
      await api.excluirPrompt(prompt.id);
      await carregar();
    } catch {
      setErro('Não foi possível excluir o prompt.');
    }
  };

  const total = prompts.length;
  const favoritos = useMemo(() => prompts.filter((p) => p.favorito).length, [prompts]);

  /**
   * Quem não tem o bônus nem chega aqui: a rota `/prompts` é guardada por
   * `RotaComBonusPrompts`. A checagem abaixo é a última linha de defesa — se ela
   * falhar, não renderizamos nada da biblioteca.
   */
  if (!usuario?.promptsIA && !usuario?.admin) return null;

  return (
    <div className="space-y-6 py-2">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="space-y-1">
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Wand2 className="size-5 text-primary" />
            Biblioteca de prompts
          </h1>
          <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">
            Guarde aqui os prompts de IA que você testou e deram certo — com categoria, modelo usado e o que
            aprendeu. Ao lado, o guia de bolso: o que é IA, o que é prompt, anatomia, métodos e erros comuns.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="contorno" onClick={() => setGuiaAberto(true)}>
            <BookOpenCheck />
            Guia e exemplos
          </Button>
          <Button onClick={abrirNovo}>
            <Plus />
            Novo prompt
          </Button>
        </div>
      </header>

      {erro ? (
        <p className="flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm text-destructive">
          <TriangleAlert className="mt-0.5 size-4 shrink-0" />
          {erro}
        </p>
      ) : null}

      {aviso ? (
        <p className="flex items-center gap-2 rounded-lg border border-primary/40 bg-primary/5 p-4 text-sm text-foreground">
          <Check className="size-4 shrink-0 text-primary" />
          {aviso}
        </p>
      ) : null}

      {/* Filtros */}
      <section className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card p-3">
        <div className="relative min-w-[220px] flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <input
            value={busca}
            onChange={(evento) => setBusca(evento.target.value)}
            placeholder="Buscar por título, objetivo, trecho ou etiqueta"
            className="h-9 w-full rounded-md border border-border bg-background pl-8 pr-3 text-sm"
          />
        </div>

        <Select
          value={categoria}
          onValueChange={(valor) => setCategoria(valor as CategoriaPrompt | 'TODAS')}
        >
          <SelectTrigger className="w-[220px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="TODAS">Todas as categorias</SelectItem>
            {CATEGORIAS_PROMPT.map((item) => (
              <SelectItem key={item} value={item}>
                {ROTULO_CATEGORIA_PROMPT[item]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Button
          variant={somenteFavoritos ? 'padrao' : 'contorno'}
          tamanho="pequeno"
          onClick={() => setSomenteFavoritos((atual) => !atual)}
        >
          <Star className={somenteFavoritos ? 'fill-current' : undefined} />
          Favoritos
        </Button>

        <span className="text-xs text-muted-foreground">
          {total} prompt(s) · {favoritos} favorito(s)
        </span>
      </section>

      {/* Lista */}
      {carregando ? (
        <p className="flex items-center justify-center gap-2 rounded-lg border border-border p-10 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Carregando sua biblioteca...
        </p>
      ) : prompts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center">
          <Sparkles className="mx-auto size-6 text-muted-foreground" />
          <p className="mt-3 text-sm font-medium">Nenhum prompt guardado ainda</p>
          <p className="mx-auto mt-1 max-w-md text-xs leading-relaxed text-muted-foreground">
            Comece pelo guia: escolha um exemplo, teste na sua ferramenta de IA, ajuste até ficar do seu jeito e
            salve aqui o texto exato que funcionou.
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-2">
            <Button variant="contorno" onClick={() => setGuiaAberto(true)}>
              <BookOpenCheck />
              Ver o guia e os exemplos
            </Button>
            <Button onClick={abrirNovo}>
              <Plus />
              Cadastrar meu prompt
            </Button>
          </div>
        </div>
      ) : (
        <ul className="grid gap-4 lg:grid-cols-2">
          {prompts.map((prompt) => (
            <li key={prompt.id} className="flex flex-col rounded-xl border border-border bg-card p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 space-y-1">
                  <h3 className="truncate text-sm font-semibold">{prompt.titulo}</h3>
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge tom="principal">{ROTULO_CATEGORIA_PROMPT[prompt.categoria]}</Badge>
                    {prompt.modelo ? <Badge className="text-[10px]">{prompt.modelo}</Badge> : null}
                    {prompt.usos > 0 ? <Badge className="text-[10px]">{prompt.usos} uso(s)</Badge> : null}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => void alternarFavorito(prompt)}
                  aria-label={prompt.favorito ? 'Remover dos favoritos' : 'Marcar como favorito'}
                  className={cn(
                    'rounded-md p-1.5 transition-colors',
                    prompt.favorito ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  <Star className={prompt.favorito ? 'size-4 fill-current' : 'size-4'} />
                </button>
              </div>

              {prompt.objetivo ? (
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{prompt.objetivo}</p>
              ) : null}

              <pre className="mt-3 max-h-40 flex-1 overflow-auto whitespace-pre-wrap rounded-md border border-border bg-muted/40 p-3 text-[11px] leading-relaxed">
{prompt.conteudo}
              </pre>

              {prompt.resultado ? (
                <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">
                  <strong className="font-semibold text-foreground">Resultado:</strong> {prompt.resultado}
                </p>
              ) : null}

              {prompt.tags.length > 0 ? (
                <p className="mt-2 flex flex-wrap gap-1">
                  {prompt.tags.map((tag) => (
                    <span key={tag} className="rounded-full bg-secondary px-2 py-0.5 text-[10px] text-muted-foreground">
                      #{tag}
                    </span>
                  ))}
                </p>
              ) : null}

              <div className="mt-3 flex flex-wrap gap-2 border-t border-border/70 pt-3">
                <Button tamanho="pequeno" variant="contorno" onClick={() => void copiar(prompt)}>
                  {copiaDo === prompt.id ? <Check /> : <Copy />}
                  {copiaDo === prompt.id ? 'Copiado' : 'Copiar'}
                </Button>
                <Button tamanho="pequeno" variant="fantasma" onClick={() => abrirEdicao(prompt)}>
                  <Pencil />
                  Editar
                </Button>
                <Button
                  tamanho="pequeno"
                  variant="fantasma"
                  className="text-destructive"
                  onClick={() => void excluir(prompt)}
                >
                  <Trash2 />
                  Excluir
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Formulário */}
      <Dialog open={formularioAberto} onOpenChange={setFormularioAberto}>
        <DialogContent
          titulo={editando ? 'Editar prompt' : 'Novo prompt'}
          descricao="Guarde o texto exatamente como funcionou — inclusive as restrições que você adicionou."
        >
          <div className="max-h-[70vh] space-y-4 overflow-y-auto pr-1">
            <div className="grid gap-4 sm:grid-cols-2">
              <Campo rotulo="Título">
                <Input
                  value={dados.titulo}
                  onChange={(evento) => setDados((atual) => ({ ...atual, titulo: evento.target.value }))}
                  placeholder="Ex.: Revisar texto sem perder meu estilo"
                  maxLength={120}
                />
              </Campo>
              <Campo rotulo="Categoria" dica={DESCRICAO_CATEGORIA_PROMPT[dados.categoria]}>
                <Select
                  value={dados.categoria}
                  onValueChange={(valor) =>
                    setDados((atual) => ({ ...atual, categoria: valor as CategoriaPrompt }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIAS_PROMPT.map((item) => (
                      <SelectItem key={item} value={item}>
                        {ROTULO_CATEGORIA_PROMPT[item]}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Campo>
            </div>

            <Campo rotulo="Objetivo" dica="Para que serve, em uma frase.">
              <Input
                value={dados.objetivo}
                onChange={(evento) => setDados((atual) => ({ ...atual, objetivo: evento.target.value }))}
                placeholder="Ex.: corrigir clareza e gramática sem mudar meu jeito de escrever"
                maxLength={240}
              />
            </Campo>

            <Campo rotulo="Prompt" dica="Cole o texto completo, com papel, tarefa, contexto e restrições.">
              <Textarea
                value={dados.conteudo}
                onChange={(evento) => setDados((atual) => ({ ...atual, conteudo: evento.target.value }))}
                rows={10}
                maxLength={8000}
                placeholder={'Aja como...\nTarefa: ...\nContexto: ...\nFormato da resposta: ...\nRestrições: ...'}
              />
            </Campo>

            <div className="grid gap-4 sm:grid-cols-2">
              <Campo rotulo="Modelo e versão" dica="A mesma frase muda de comportamento entre ferramentas e versões.">
                <Input
                  value={dados.modelo}
                  onChange={(evento) => setDados((atual) => ({ ...atual, modelo: evento.target.value }))}
                  placeholder="Ex.: GPT-4o, agosto/2026"
                  maxLength={80}
                />
              </Campo>
              <Campo rotulo="Etiquetas" dica="Separe por vírgula. Ajuda a achar depois.">
                <Input
                  value={dados.tags.join(', ')}
                  onChange={(evento) =>
                    setDados((atual) => ({
                      ...atual,
                      tags: evento.target.value
                        .split(',')
                        .map((tag) => tag.trim())
                        .filter(Boolean)
                        .slice(0, 8),
                    }))
                  }
                  placeholder="cliente, e-mail, tom"
                />
              </Campo>
            </div>

            <Campo rotulo="Resultado e o que ajustei" dica="Anote o que funcionou e o ajuste que fez diferença.">
              <Textarea
                value={dados.resultado}
                onChange={(evento) => setDados((atual) => ({ ...atual, resultado: evento.target.value }))}
                rows={3}
                maxLength={1200}
                placeholder="Ex.: funcionou bem pedindo máximo de 150 palavras; sem isso, saía longo demais."
              />
            </Campo>

            <LinhaSwitch
              id="prompt-favorito"
              rotulo="Marcar como favorito"
              descricao="Os favoritos aparecem no topo da lista."
              checked={dados.favorito}
              onCheckedChange={(valor) => setDados((atual) => ({ ...atual, favorito: valor }))}
            />

            <div className="flex flex-wrap justify-end gap-2 pt-1">
              <Button variant="fantasma" onClick={() => setFormularioAberto(false)}>
                Cancelar
              </Button>
              <Button onClick={salvar} disabled={salvando || dados.titulo.trim().length < 2 || dados.conteudo.trim().length < 10}>
                {salvando ? <Loader2 className="animate-spin" /> : <Check />}
                {editando ? 'Salvar alterações' : 'Guardar prompt'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Guia + exemplos */}
      <Dialog open={guiaAberto} onOpenChange={setGuiaAberto}>
        <DialogContent
          titulo="Guia de IA e prompts"
          descricao="Material de apoio para usar IA com método — e exemplos prontos para copiar e adaptar."
        >
          <div className="max-h-[70vh] space-y-6 overflow-y-auto pr-1">
            <section className="space-y-4 rounded-lg border border-border bg-muted/30 p-4">
              {GUIA_RESUMIDO.map((bloco) => (
                <article key={bloco.titulo} className="space-y-2">
                  <h4 className="text-sm font-semibold">{bloco.titulo}</h4>
                  {bloco.paragrafos.map((paragrafo) => (
                    <p key={paragrafo} className="text-xs leading-relaxed text-muted-foreground">
                      {paragrafo}
                    </p>
                  ))}
                  {bloco.itens ? (
                    <ul className="space-y-1 pl-1">
                      {bloco.itens.map((item) => (
                        <li key={item} className="flex gap-2 text-xs leading-relaxed text-muted-foreground">
                          <span className="mt-1.5 size-1 shrink-0 rounded-full bg-primary/60" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </article>
              ))}
            </section>

            {CATEGORIAS_PROMPT.map((cat) => {
              const doGrupo = exemplos.filter((exemplo) => exemplo.categoria === cat);
              if (doGrupo.length === 0) return null;
              return (
                <section key={cat} className="space-y-3">
                  <h4 className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    {ROTULO_CATEGORIA_PROMPT[cat]}
                  </h4>
                  {doGrupo.map((exemplo) => (
                    <article key={exemplo.id} className="space-y-2 rounded-lg border border-border bg-card p-4">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <h5 className="text-sm font-semibold">{exemplo.titulo}</h5>
                          <p className="text-xs text-muted-foreground">{exemplo.objetivo}</p>
                        </div>
                        <Button tamanho="pequeno" variant="contorno" onClick={() => usarExemplo(exemplo)}>
                          <Plus />
                          Usar como base
                        </Button>
                      </div>
                      <pre className="max-h-52 overflow-auto whitespace-pre-wrap rounded-md border border-border bg-muted/40 p-3 text-[11px] leading-relaxed">
{exemplo.conteudo}
                      </pre>
                      <p className="text-[11px] leading-relaxed text-muted-foreground">
                        <strong className="font-semibold text-foreground">Dica:</strong> {exemplo.dica}
                      </p>
                    </article>
                  ))}
                </section>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Resumo do guia exibido no site; o texto completo sai impresso no caderno. */
const GUIA_RESUMIDO = [
  {
    titulo: 'O que é inteligência artificial',
    paragrafos: [
      'IA é o nome dado a programas capazes de fazer tarefas que antes exigiam uma pessoa: entender texto, escrever, resumir, comparar, classificar.',
      'Os assistentes de conversa são modelos de linguagem: aprenderam a prever a palavra seguinte lendo uma quantidade enorme de textos. Escrevem com naturalidade, mas não "sabem" as coisas como uma pessoa sabe.',
    ],
    itens: [
      'Bom para: rascunhar, organizar, resumir, traduzir, comparar, gerar variações.',
      'Ruim para: decidir por você, dar números oficiais, tratar dado sigiloso.',
      'Regra de ouro: a IA propõe, você decide — e revisa.',
    ],
  },
  {
    titulo: 'O que é um prompt',
    paragrafos: [
      'Prompt é o pedido que você faz para a IA. A diferença entre um pedido curto e um prompt bem escrito é quantas decisões você tomou antes de pedir.',
    ],
    itens: [
      'Pergunta: "faz um texto sobre produtividade".',
      'Prompt: "Escreva um e-mail de 120 palavras para a equipe de vendas, tom direto e cordial, lembrando que o fechamento é na sexta e os relatórios vão até quinta, 18h."',
    ],
  },
  {
    titulo: 'Anatomia de um bom prompt',
    paragrafos: ['Quando a resposta sai errada, quase sempre falta uma destas peças.'],
    itens: [
      'Papel: quem a IA deve ser ("aja como revisor de textos").',
      'Tarefa: o que fazer, com verbo no imperativo.',
      'Contexto: o que ela não tem como adivinhar.',
      'Formato: lista, tabela, e-mail pronto, JSON.',
      'Restrições: tamanho, tom, o que evitar, idioma.',
      'Exemplos: um ou dois modelos do resultado ideal.',
      'Critério de sucesso: como você julga se ficou bom.',
    ],
  },
  {
    titulo: 'Métodos para montar prompts',
    paragrafos: ['Todos organizam as mesmas peças; escolha o que combina com a tarefa.'],
    itens: [
      'Persona + público.',
      'Tarefa + formato.',
      'Passo a passo antes da resposta final.',
      'Poucos exemplos (entrada → saída).',
      'Rascunho + crítica: gere, peça 5 problemas, reescreva.',
      'Entrevista: a IA pergunta antes de responder.',
      'Restrições duras: sem clichê, sem marca, máximo de caracteres.',
      'Iteração guiada: ajuste uma coisa por vez e guarde o que funcionou.',
    ],
  },
  {
    titulo: 'Erros comuns',
    paragrafos: ['A correção é sempre a mesma: dizer explicitamente o que você quer.'],
    itens: [
      'Pedir tudo de uma vez.',
      'Não dizer o público.',
      'Não definir o formato.',
      'Aceitar a primeira resposta.',
      'Não revisar números, datas e referências.',
      'Enviar dado sensível para o chat.',
    ],
  },
];
